import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef, Table } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import DateTimePicker from '../../components/DateTimePicker';
import ReportTile from '../../components/reports/ReportTile';
import api from '../../services/api';
import { config } from '../../config/config';
import styles from './OosDeferments.module.css';

/**
 * Fuel / No Income — contractors who fueled in a week they earned nothing in.
 *
 * One row per driver-week, flat: the week is the unit the fuel file and the
 * settlement both work in, so a driver flagged in three weeks is three things
 * to look at, not one row to open. That is why there is no expander — every
 * figure a row has is in the row.
 *
 * Two calendars bound the span, and both offer only week-ending Sundays we
 * actually hold a fuel file for — any other day is a question with no answer
 * behind it. A year of those Sundays is offered, but the span is loaded from
 * the server rather than filtered here, because computing a week means
 * scanning the pay-line table for it: a year is reachable, never the default.
 */

type WeekState = 'live' | 'pending' | 'settled';
type WeekStatus = 'no_income' | 'shortfall';

interface FuelWeek {
  week_ending: string;
  state: WeekState;
  status: WeekStatus;
  fuel_amount: string;
  net_pay: string;
  pay_lines: number;
  shortfall: string;
  invoice_total: string | null;
  invoice_date: string | null;
  fuel_deferred: string;
}

interface FuelDriver {
  unitnumber: string;
  driver_name: string;
  drivertype: string;
  region: string;
  dispatcher: string | null;
  companyrep: string | null;
  unit_status: string;
  status_date: string | null;
  weeks: FuelWeek[];
}

interface FuelDashboard {
  as_of: string;
  /** Every week ending the pickers may offer — a year of them. */
  weeks: string[];
  latest_week: string | null;
  /** The span the server actually reported on. */
  from_week: string | null;
  to_week: string | null;
  drivers: FuelDriver[];
}

/** A grid line: one driver-week, or one driver once grouping collapses them.
 *
 * `balance` and `weeks_count` are derived rather than raw so a grouped line and
 * a single-week line are the same shape — every column reads the same field
 * either way instead of branching on whether grouping is on. */
type FuelRow = Omit<FuelDriver, 'weeks'> & FuelWeek & {
  id: string;
  /** Outstanding fuel, or null while no week in the line has settled. */
  balance: number | null;
  weeks_count: number;
};

const dollars = (value: string | number) =>
  Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const dateLabel = (value?: string | null) => {
  if (!value || value.startsWith('0000')) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};
/** Cents as integers — a running total of dollar floats drifts. */
const sumMoney = (values: number[]) =>
  values.reduce((total, value) => total + Math.round(value * 100), 0) / 100;

/** The outstanding fuel balance of a set of weeks, or null if none has settled.
 *
 * Until a settlement runs, nothing has been deferred and the raw figure is
 * $0.00 — which reads as "collected in full" when it actually means "not yet
 * determined". Everything that shows or totals the balance goes through here
 * so the column, the tile, the totals row and the Hide Paid In Full switch
 * cannot disagree. */
const balanceOf = (weeks: Pick<FuelWeek, 'state' | 'fuel_deferred'>[]) => {
  const settled = weeks.filter(week => week.state === 'settled');
  return settled.length ? sumMoney(settled.map(week => Number(week.fuel_deferred))) : null;
};

/** Local Y-M-D. toISOString would shift the date a day west of UTC. */
const toIso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
/** Midnight local, so a week ending never lands on the previous day. */
const fromIso = (iso: string) => new Date(`${iso}T00:00:00`);

export default function FuelNoIncome() {
  const [report, setReport] = useState<FuelDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unitStatus, setUnitStatus] = useState('');
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [showPartial, setShowPartial] = useState(true);
  const [hidePaid, setHidePaid] = useState(false);
  const [groupByDriver, setGroupByDriver] = useState(false);

  // The span drives a scan of the pay-line table, so it is the server that
  // narrows it, not a filter here: a year of weeks is offered but only the
  // weeks asked for are ever computed. An unparameterised first load gets the
  // server's recent default back, and the pickers adopt the span it reports.
  const refresh = useCallback(async (span?: { from: string; to: string }, signal?: AbortSignal) => {
    setLoading(true);
    setError('');
    try {
      const params = span?.from && span?.to
        ? { view: 'fuel', from: span.from, to: span.to }
        : { view: 'fuel' };
      const { data } = await api.get<FuelDashboard>('/oos-deferments', { params, signal });
      if (!signal?.aborted) {
        setReport(data);
        setRange({ from: data.from_week ?? '', to: data.to_week ?? '' });
      }
    } catch {
      if (!signal?.aborted) {
        setReport(null);
        setError('Unable to load fuel exposure. Please retry or check your access with an administrator.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    void refresh(undefined, controller.signal);
    return () => controller.abort();
  }, [refresh]);

  /** Moving either end reloads; the other end holds. */
  const pickWeek = (end: 'from' | 'to') => (value: Date | null) => {
    if (!value) return;
    const next = { ...range, [end]: toIso(value) };
    // A back-to-front pick collapses onto the day chosen rather than loading
    // nothing — the server swaps them, and the inputs follow its answer.
    void refresh(next.from <= next.to ? next : { from: next[end], to: next[end] });
  };

  /** Shortcuts inside both calendars. "Current week" is the week everyone comes
   *  here for — the fuel has gone out and the settlement has not collected it
   *  yet — and it sets BOTH ends, so it is one click from any span back to the
   *  live week. Selecting a shortcut closes the calendar. */
  const dateShortcuts = useMemo(() => {
    const weeks = report?.weeks ?? [];
    if (!weeks.length) return [];
    const latest = weeks[0];
    const span = (from: string) => () => void refresh({ from, to: latest });
    return [
      { label: 'Current week', onSelect: span(latest) },
      // weeks is newest-first, so index N-1 is N weeks back — clamped to what
      // was actually loaded rather than assuming a full year is there.
      { label: 'Last 4 weeks', onSelect: span(weeks[Math.min(3, weeks.length - 1)]) },
      { label: 'Last 13 weeks', onSelect: span(weeks[Math.min(12, weeks.length - 1)]) },
      // Year to date: the oldest loaded week still inside the latest week's
      // calendar year. The pickers only reach back a year, so in a late month
      // this is most of what is loaded — and the slowest span on offer, since
      // every week in it means scanning the pay lines for that week.
      { label: 'YTD', onSelect: (() => {
          const thisYear = weeks.filter(week => week.slice(0, 4) === latest.slice(0, 4));
          return span(thisYear[thisYear.length - 1] ?? weeks[weeks.length - 1]);
        })() },
    ];
  }, [report, refresh]);

  const statuses = useMemo(
    () => [...new Set(report?.drivers.map(d => d.unit_status) ?? [])].sort(), [report]);

  // Only the Sundays we hold a fuel file for are selectable, and the calendar
  // is bounded to that span so there is nothing else to wander into.
  const weekSet = useMemo(() => new Set(report?.weeks ?? []), [report]);
  const isWeekEnding = useCallback((date: Date) => weekSet.has(toIso(date)), [weekSet]);
  const bounds = useMemo(() => {
    const weeks = report?.weeks ?? [];
    if (!weeks.length) return { min: undefined, max: undefined };
    return { min: fromIso(weeks[weeks.length - 1]), max: fromIso(weeks[0]) };
  }, [report]);

  const flatRows = useMemo<FuelRow[]>(() => {
    const rows: FuelRow[] = [];
    for (const driver of report?.drivers ?? []) {
      if (unitStatus && driver.unit_status !== unitStatus) continue;
      const { weeks, ...details } = driver;
      for (const week of weeks) {
        if (!showPartial && week.status !== 'no_income') continue;
        // "Paid in full" is a SETTLED week with nothing left owing. An
        // unsettled week has no balance at all (balanceOf gives null, never 0),
        // so it is never hidden — which matters because the default view is the
        // newest and least settled week there is.
        if (hidePaid && balanceOf([week]) === 0) continue;
        rows.push({ ...details, ...week, id: `${driver.unitnumber}-${week.week_ending}`,
                    balance: balanceOf([week]), weeks_count: 1 });
      }
    }
    // Unit number ascending, and a driver with more than one uncovered week
    // keeps those as separate lines, newest first.
    return rows.sort((a, b) =>
      (Number(a.unitnumber) || 0) - (Number(b.unitnumber) || 0) ||
      a.unitnumber.localeCompare(b.unitnumber) ||
      b.week_ending.localeCompare(a.week_ending));
  }, [report, unitStatus, showPartial, hidePaid]);

  /** Group by driver: one line per unit, the weeks summed into it. */
  const visible = useMemo<FuelRow[]>(() => {
    if (!groupByDriver) return flatRows;
    const byDriver = new Map<string, FuelRow[]>();
    for (const row of flatRows) {
      const group = byDriver.get(row.unitnumber);
      if (group) group.push(row); else byDriver.set(row.unitnumber, [row]);
    }
    // flatRows is already sorted, so lines[0] is the driver's newest week and
    // carries the driver-level details the grouped line shows unchanged.
    return [...byDriver.values()].map(lines => ({
      ...lines[0],
      id: lines[0].unitnumber,
      weeks_count: lines.length,
      fuel_amount: sumMoney(lines.map(line => Number(line.fuel_amount))).toFixed(2),
      net_pay: sumMoney(lines.map(line => Number(line.net_pay))).toFixed(2),
      shortfall: sumMoney(lines.map(line => Number(line.shortfall))).toFixed(2),
      balance: balanceOf(lines),
    }));
  }, [flatRows, groupByDriver]);

  // Always off the ungrouped rows, so the tiles describe the same underlying
  // set whichever way the grid is arranged.
  const totals = useMemo(() => ({
    drivers: new Set(flatRows.map(row => row.unitnumber)).size,
    zeroIncome: flatRows.filter(row => row.status === 'no_income').length,
    shortfall: sumMoney(flatRows.map(row => Number(row.shortfall))),
    balance: sumMoney(flatRows.map(row => row.balance).filter((v): v is number => v !== null)),
  }), [flatRows]);

  /** Totals row. Reads `getFilteredRowModel` rather than `visible` so typing in
   *  a column's Filter box moves the bottom line with it. */
  const moneyFooter = (pick: (row: FuelRow) => number | null) =>
    ({ table }: { table: Table<FuelRow> }) => {
      const values = table.getFilteredRowModel().rows
        .map(tableRow => pick(tableRow.original))
        .filter((value): value is number => value !== null);
      return values.length ? dollars(sumMoney(values)) : '—';
    };

  const columns = useMemo<ColumnDef<FuelRow>[]>(() => [
    { accessorKey: 'unitnumber', header: 'Driver', size: 95, sortingFn: 'alphanumeric',
      cell: ({ getValue }) => {
      const driver = getValue<string>();
      const unit = parseInt(driver, 10);
      if (!driver || Number.isNaN(unit)) return driver || '—';
      return <a href={`${config.V2_URL}/safety/drivers/${unit}/driver`}
        target="_blank" rel="noopener noreferrer" title="View in CMS" onClick={event => event.stopPropagation()}
        className="font-semibold text-blue-600 hover:underline dark:text-blue-400">{driver}</a>;
    },
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows;
        return `${rows.length} ${groupByDriver ? 'driver' : 'row'}${rows.length === 1 ? '' : 's'}`;
      } },
    { accessorKey: 'driver_name', header: 'Name', size: 180 },
    // The money first, in the order it is read: what the week was, what the
    // fuel cost, what they earned against it, the gap, and what is still owed.
    // Grouped, one line stands for several weeks, so it says how many instead
    // of naming one of them and implying the others are not there.
    { accessorKey: 'week_ending', header: 'Week ending', size: 120,
      cell: ({ row, getValue }) => row.original.weeks_count > 1
        ? `${row.original.weeks_count} weeks`
        : dateLabel(getValue<string>()) },
    { id: 'fuel_amount', accessorFn: row => Number(row.fuel_amount), header: 'Fuel charged', size: 125,
      cell: ({ getValue }) => dollars(getValue<number>()),
      footer: moneyFooter(row => Number(row.fuel_amount)) },
    { id: 'net_pay', accessorFn: row => Number(row.net_pay), header: 'Net pay', size: 115,
      cell: ({ getValue }) => dollars(getValue<number>()),
      footer: moneyFooter(row => Number(row.net_pay)) },
    { id: 'shortfall', accessorFn: row => Number(row.shortfall), header: 'Short by', size: 115,
      cell: ({ getValue }) => dollars(getValue<number>()),
      footer: moneyFooter(row => Number(row.shortfall)) },
    { id: 'fuel_deferred', accessorFn: row => row.balance, header: 'Current Balance', size: 140,
      cell: ({ getValue }) => {
        const balance = getValue<number | null>();
        return balance === null ? '—' : dollars(balance);
      },
      footer: moneyFooter(row => row.balance) },
    { accessorKey: 'unit_status', header: 'Unit status', size: 135, cell: ({ getValue }) => {
      const value = getValue<string>();
      return value === 'TERMINATED' ? <span className={styles.terminated}>{value}</span> : value;
    } },
    // Whichever date explains the status — terminated on, or out of service
    // since. Its own column, so it sorts and exports as a date rather than as a
    // fragment of the status text.
    { accessorKey: 'status_date', header: 'Status Date', size: 130,
      cell: ({ getValue }) => dateLabel(getValue<string | null>()) },
    { accessorKey: 'dispatcher', header: 'Dispatcher', size: 150 },
    { accessorKey: 'companyrep', header: 'Company rep', size: 150 },
    { accessorKey: 'region', header: 'Region', size: 125 },
  ], [groupByDriver]);

  return <>
    <header className="shrink-0 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-xl font-semibold">Fuel / No Income</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Contractors whose fuel card charges were not covered by their net pay that week.
        </p>
      </div>
      <button type="button" className={styles.refresh} onClick={() => void refresh(range)} disabled={loading}>
        <ArrowPathIcon width={16} aria-hidden="true" />{loading ? 'Loading…' : 'Refresh'}
      </button>
    </header>
    {error && <div role="alert" className={styles.error}>{error}</div>}
    <FilterBar wrap label="" className="shrink-0">
      <FilterBar.Group label="Week ending from">
        <DateTimePicker mode="date" selectionType="single" labelType="none" ariaLabel="Week ending from"
          value={range.from ? fromIso(range.from) : null}
          onChange={value => pickWeek('from')(value instanceof Date ? value : null)}
          minDate={bounds.min} maxDate={bounds.max} isDateSelectable={isWeekEnding}
          calendarShortcuts={dateShortcuts}
          autoApply usePortal inputVariant="compact" className="w-36" />
      </FilterBar.Group>
      <FilterBar.Group label="to">
        <DateTimePicker mode="date" selectionType="single" labelType="none" ariaLabel="Week ending to"
          value={range.to ? fromIso(range.to) : null}
          onChange={value => pickWeek('to')(value instanceof Date ? value : null)}
          minDate={bounds.min} maxDate={bounds.max} isDateSelectable={isWeekEnding}
          calendarShortcuts={dateShortcuts}
          autoApply usePortal inputVariant="compact" className="w-36" />
      </FilterBar.Group>
      <FilterBar.Group label="Unit status">
        <SingleSelect ariaLabel="Unit status" value={unitStatus} onChange={setUnitStatus}
          options={[{ value: '', label: 'All statuses' }, ...statuses.map(s => ({ value: s, label: s }))]}
          size="sm" className="w-48" usePortal />
      </FilterBar.Group>
      {/* Two-state switch: the side that is lit is the side being shown, so the
          label says what you are looking at rather than what clicking would do. */}
      <button type="button" role="switch" aria-checked={showPartial} className={styles.balanceToggle}
        aria-label="Show Partial Income" onClick={() => setShowPartial(value => !value)}>
        <span className={showPartial ? styles.modeOff : styles.modeOn}>No Income</span>
        <span className={styles.toggleTrack} aria-hidden="true"><span /></span>
        <span className={showPartial ? styles.modeOn : styles.modeOff}>Partial Income</span>
      </button>
      <button type="button" role="switch" aria-checked={hidePaid} className={styles.balanceToggle}
        onClick={() => setHidePaid(value => !value)}>
        <span className={styles.toggleTrack} aria-hidden="true"><span /></span>
        Hide Paid In Full
      </button>
      <button type="button" role="switch" aria-checked={groupByDriver} className={styles.balanceToggle}
        onClick={() => setGroupByDriver(value => !value)}>
        <span className={styles.toggleTrack} aria-hidden="true"><span /></span>
        Group by driver
      </button>
    </FilterBar>
    <section className="shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-2" aria-label="Totals for the selected weeks">
      <ReportTile label="Drivers to review" value={loading || !report ? '—' : totals.drivers.toLocaleString()}
        tone="blue" help="Contractors with at least one flagged week in the selected range." />
      <ReportTile label="Earned nothing" value={loading || !report ? '—' : totals.zeroIncome.toLocaleString()}
        tone="red" help="Driver-weeks with no pay lines at all — the 9394 pattern." />
      <ReportTile label="Fuel not covered" value={loading || !report ? '—' : dollars(totals.shortfall)}
        tone="amber" help="Fuel charged above net pay across the selected weeks." />
      <ReportTile label="Current Balance" value={loading || !report ? '—' : dollars(totals.balance)}
        tone="purple" help="Fuel from these weeks the settlement could not collect, still open as a FUEL CARD deferment." />
    </section>
    <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
      Net pay is the settlement's own arithmetic — the rate less 30% commission, plus fuel surcharge — so
      a row here is money that genuinely did not cover itself. One row per driver per week.
    </p>
    <div className="flex-1 min-h-0">
      <TanTable<FuelRow> data={visible} columns={columns} settingsKey="safety-fuel-no-income"
        fieldName="safety-fuel-no-income" cardTitle="Fuel without income" loading={loading}
        // Per-column filters are on: Region, Unit status, Dispatcher and the
        // rest are exactly the columns someone narrows by, and the totals row
        // reads the table's filtered rows, so the bottom line follows them.
        enableSearch enableColumnFilters enableExcelExport enableShowHideCols enableRowsPerPage
        enableZebraStriping disableVirtualization fillContainer showColumnFooter
        defaultSorting={[{ id: 'unitnumber', desc: false }]}
        getRowId={row => row.id} />
    </div>
    {!loading && report && visible.length === 0 &&
      <p role="status" className={styles.emptyDetail}>
        No contractors match these filters for weeks ending {dateLabel(range.from)} to {dateLabel(range.to)}.
      </p>}
    <details className={styles.definitions}>
      <summary>About these figures{report?.latest_week && ` · Latest fuel week ending ${dateLabel(report.latest_week)}`}</summary>
      <p>Fuel comes from the weekly Comdata card file loaded into driver pay, one total per contractor per
        week, dated by the Sunday the week ended — which is why only those Sundays can be picked. A driver
        appears here when their net pay for that same week came to less than the fuel they charged.</p>
      <p>Net pay follows the settlement: the pay line's rate, less 30% commission where the line is
        commissioned, plus fuel surcharge at the week's rate from the fuel index. Lines marked DO NOT PAY
        or DEFER are not income. For an OTR driver the surcharge usually exceeds the commission, so net
        runs above the raw rate — this is the same figure payroll works from, not an estimate.</p>
      <p><strong>Current Balance</strong> is what that week's fuel still has outstanding as a FUEL CARD
        deferment, after any payments against it, and it counts only settled weeks. A week whose
        settlement has not run yet shows <strong>—</strong> rather than $0.00: nothing has been deferred
        from it, but nothing has been collected either, and a zero there would read as "paid in full"
        when the money has not been asked for yet.</p>
      <p><strong>Group by driver</strong> collapses a driver's weeks into one line, summing the fuel, the
        net pay and the shortfall, and showing how many weeks went into it. The totals row at the bottom
        follows whatever is on screen, including the per-column filter boxes, so it always adds up to
        what you are looking at.</p>
      <p>Turn off <strong>Show Partial Income</strong> to leave only the driver-weeks with no pay at all.
        <strong> Hide Paid In Full</strong> drops the weeks that settled and left nothing owing, so what
        remains is money still out there. A week that has not settled yet is never treated as paid in
        full — it shows a zero balance only because nothing has been deferred from it yet, so the newest
        week stays put either way.</p>
      <p><strong>Status Date</strong> is the date behind the unit's current status: when a terminated
        unit was terminated, or when an out-of-service unit went out. It is blank for a unit in service.
        Read against the week ending, it says whether the fuel went out before the driver stopped
        earning or after.</p>
      <p>Terminated units are deliberately kept. A contractor who left owing fuel is the outcome this
        page exists to get ahead of, and the deferment stays collectable only while they are still
        driving.</p>
    </details>
  </>;
}
